import type { SelectProps } from 'antd'
import type { FocusEvent, ReactNode } from 'react'
import {
  type Control,
  Controller,
  type FieldErrors,
  type UseFormRegister,
} from 'react-hook-form'
import type { MaskedInputProps } from 'react-text-mask'

import AtomCheckbox from '@/components/Atoms/AtomCheckbox/AtomCheckbox'
import AtomFormInput from '@/components/Atoms/AtomFormInput/AtomFormInput'
import AtomSelect from '@/components/Atoms/AtomSelect/AtomSelect'
import HoverElement from '@/components/Atoms/HoverElement/HoverElement'
import type {
  FormInputControlType,
  FormInputNameType,
  FormInputRegisterType,
  OptionType,
  SelectFormControlType,
} from '@/types'

import type { ConfigFormInput } from '../lib/configFormModel'
import BooleanSelect from './BooleanSelect'

export type ConfigFieldContext = {
  control: Control<ConfigFormInput>
  register: UseFormRegister<ConfigFormInput>
  errors: FieldErrors<ConfigFormInput>
  isOrganisationSelected: boolean
}

type ConfigTextFieldProps = {
  context: ConfigFieldContext
  name: FormInputNameType
  title: ReactNode
  required?: boolean
  placeholder?: string
  mask?: MaskedInputProps['mask']
  secret?: boolean
  error?: string
  trailingText?: string
  autoComplete?: string
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void
}

export function ConfigTextField({
  context,
  name,
  title,
  required,
  placeholder,
  mask,
  secret,
  error,
  trailingText,
  autoComplete = 'off',
  onBlur,
}: ConfigTextFieldProps) {
  return (
    <AtomFormInput
      layout="stacked"
      idPrefix="config-"
      location="stockImportConfig"
      isOrganisationSelected={context.isOrganisationSelected}
      name={name}
      title={title}
      required={required}
      placeholder={placeholder}
      mask={mask}
      inputMode={mask ? 'numeric' : undefined}
      secret={secret}
      error={error}
      trailingText={trailingText}
      autoComplete={autoComplete}
      onBlur={onBlur}
      errors={context.errors}
      register={context.register as FormInputRegisterType}
      control={context.control as FormInputControlType}
    />
  )
}

type ConfigSelectName = NonNullable<
  Parameters<typeof AtomSelect>[0]['formName']
>

type ConfigSelectFieldProps = {
  context: ConfigFieldContext
  name: ConfigSelectName
  title: ReactNode
  options: OptionType[]
  required?: boolean
  placeholder?: string
  multiple?: boolean
  allowClear?: boolean
  loading?: boolean
  disabled?: boolean
  notFoundContent?: ReactNode
  optionRender?: SelectProps['optionRender']
  onChange?: (value: string) => void
}

export function ConfigSelectField({
  context,
  name,
  title,
  options,
  required,
  placeholder,
  multiple = false,
  allowClear = false,
  loading,
  disabled,
  notFoundContent,
  optionRender,
  onChange,
}: ConfigSelectFieldProps) {
  return (
    <HoverElement>
      <AtomSelect
        layout="stacked"
        fullWidth
        valueMode="value"
        openByHover={false}
        showSearch
        allowClear={allowClear}
        isOrganisationSelected={context.isOrganisationSelected}
        title={title}
        required={required}
        placeholder={placeholder}
        options={options}
        mode={multiple ? 'multiple' : undefined}
        maxTagCount={multiple ? 'responsive' : undefined}
        loading={loading}
        disabled={disabled}
        notFoundContent={notFoundContent}
        optionRender={optionRender}
        formName={name}
        formErrors={context.errors}
        formControl={context.control as SelectFormControlType}
        onChange={(value) => {
          if (typeof value === 'string') onChange?.(value)
        }}
      />
    </HoverElement>
  )
}

type ConfigCheckboxName =
  | 'excludeWeekends'
  | 'useSsl'
  | 'detectBrandInDescription'

type ConfigCheckboxFieldProps = {
  context: ConfigFieldContext
  name: ConfigCheckboxName
  children: ReactNode
}

export function ConfigCheckboxField({
  context,
  name,
  children,
}: ConfigCheckboxFieldProps) {
  return (
    <Controller
      control={context.control}
      name={name}
      render={({ field }) => (
        <AtomCheckbox
          checked={field.value}
          onChange={field.onChange}
          isOrganisationSelected={context.isOrganisationSelected}
        >
          {children}
        </AtomCheckbox>
      )}
    />
  )
}

type ConfigBooleanSelectFieldProps = {
  context: ConfigFieldContext
  name: ConfigCheckboxName
  title: ReactNode
  trueLabel: string
  falseLabel: string
}

export function ConfigBooleanSelectField({
  context,
  name,
  ...labels
}: ConfigBooleanSelectFieldProps) {
  return (
    <Controller
      control={context.control}
      name={name}
      render={({ field }) => (
        <BooleanSelect
          {...labels}
          value={field.value}
          onChange={field.onChange}
          isOrganisationSelected={context.isOrganisationSelected}
        />
      )}
    />
  )
}
